/**
 * JotRx Patient Card
 *
 * Minimal CTA button on the patient detail page.
 * Text and action adapt to the current JotRx onboarding state.
 *
 * When fully set up:
 * - If patient is connected to DoseSpot → opens patient-specific SSO
 * - If patient is NOT connected → opens the patient connect modal first
 */

import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import { type JotRxSetupState, useJotRxSetupState } from '../hooks/useJotRxSetupState';
import { useEPrescriptionStatus, useLazyGetPatientSsoQuery } from '../store';

import { EPrescriptionPatientConnectModal } from './EPrescriptionPatientConnectModal';

interface PatientData {
  firstName?: string;
  lastName?: string;
  dob?: string;
  gender?: string;
  phone?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

interface JotRxPatientCardProps {
  patientId: string;
  patientData?: PatientData;
}

function getButtonLabel(state: JotRxSetupState, isPatientConnected: boolean): string {
  switch (state) {
    case 'not_connected':
    case 'practice_connected':
      return 'Set Up E-Prescribing';
    case 'registered':
      return 'Complete ID Proofing to ePrescribe!';
    case 'idp_complete':
      return 'Setup TFA to ePrescribe!';
    case 'fully_setup':
      return isPatientConnected ? 'Send Prescription' : 'Set Up Patient for E-Rx';
  }
}

function shouldOpenSso(state: JotRxSetupState): boolean {
  return state === 'fully_setup';
}

export function JotRxPatientCard({ patientId, patientData }: JotRxPatientCardProps) {
  const navigate = useNavigate();
  const { setupState, isJotRxEnabled, isJotRxPurchasable, isLoading, cta } = useJotRxSetupState();
  const { jotrx } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const [getPatientSso] = useLazyGetPatientSsoQuery();
  const { isPatientConnected, refetch: refetchStatus } = useEPrescriptionStatus({ patientId });
  const [showConnectModal, setShowConnectModal] = useState(false);

  const isVisible = (isJotRxEnabled || isJotRxPurchasable) && !isLoading;

  const openPatientSso = useCallback(async () => {
    const ssoWindow = window.open('about:blank', '_blank');
    if (!ssoWindow) {
      enqueueSnackbar('Popup blocked — please allow popups for this site.', { variant: 'warning' });
      return;
    }
    try {
      const result = await getPatientSso({ patientId }).unwrap();
      if (result.sso_url) {
        ssoWindow.opener = null;
        ssoWindow.location.href = result.sso_url;
        jotrx.prescribeOpened({ source: 'patient_detail', patient_id: patientId });
        return;
      }
      ssoWindow.close();
    } catch {
      ssoWindow.close();
      enqueueSnackbar('Failed to open DoseSpot. Please try again.', { variant: 'error' });
    }
  }, [getPatientSso, patientId, enqueueSnackbar, jotrx]);

  const handleClick = useCallback(async () => {
    jotrx.setupStarted({ source: 'patient_detail', setup_state: setupState });

    if (shouldOpenSso(setupState)) {
      if (isPatientConnected) {
        await openPatientSso();
      } else {
        setShowConnectModal(true);
      }
      return;
    }

    navigate(cta.route);
  }, [jotrx, setupState, isPatientConnected, openPatientSso, cta.route, navigate]);

  const handleConnectSuccess = useCallback(() => {
    refetchStatus();
    openPatientSso();
  }, [refetchStatus, openPatientSso]);

  if (!isVisible) return null;

  const needsPatientSetup = setupState === 'fully_setup' && !isPatientConnected;

  return (
    <>
      <Button
        variant={needsPatientSetup ? 'outlined' : 'contained'}
        fullWidth
        color="secondary"
        onClick={handleClick}
        sx={{
          py: 1.25,
          fontWeight: 600,
          borderRadius: 1.5,
          textTransform: 'none',
          ...(needsPatientSetup
            ? { boxShadow: 0, borderWidth: 2 }
            : { boxShadow: 1 }),
        }}
      >
        {getButtonLabel(setupState, isPatientConnected)}
      </Button>

      <EPrescriptionPatientConnectModal
        open={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        patientId={patientId}
        patientData={patientData}
        onSuccess={handleConnectSuccess}
      />
    </>
  );
}
